<script setup>
/* Rahmen fuer Dialoge: Abdunkler, Kasten, Kopfzeile, Fussleiste.
 *
 * Vorher stand dieselbe Konstruktion in jedem Dialog erneut. Ab dem dritten
 * Mal lohnt eine eigene Komponente — sonst laeuft die Gestaltung auseinander,
 * sobald einer davon angefasst wird.
 *
 * ConfirmDialog.vue benutzt den Rahmen bewusst NICHT: Die Rueckfrage muss
 * ueber allen anderen Dialogen liegen und hat mit `role="alertdialog"` eine
 * andere Bedeutung fuer Vorleseprogramme. Ausserdem teleportiert
 * ConfirmDialog nicht (siehe unten) und braucht diesen Fix gar nicht.
 *
 * Hell-/Dunkel-Fix (2026-09-29): Die Theme-Variablen in main.css stehen
 * unter :root (= Dunkel) und .v-theme--light. Vuetify setzt .v-theme--light
 * nur auf die Wurzel von <v-app> (.v-application), nicht auf <body>. Der
 * Teleport unten haengt den Dialog hinter .v-application ins Dokument —
 * dort greift kein .v-theme--light mehr, main.css faellt auf :root (Dunkel)
 * zurueck, egal welches Thema eingestellt ist. Deshalb hier selbst die
 * Themenklasse holen (useTheme(), wie in useAppTheme.js) und auf ein
 * gemeinsames Wurzelelement im Teleport setzen, statt sie vom umgebenden DOM
 * zu erben. */
import { onMounted, onBeforeUnmount } from 'vue';
import { useTheme } from 'vuetify';
import { useConfirm } from '../composables/useConfirm.js';

const { state: confirmState } = useConfirm();
const { themeClasses } = useTheme();

defineProps({
  title: { type: String, default: '' },
  width: { type: Number, default: 560 },
  /* Breite ab 1024 px (Desktop). Ohne Angabe gilt auch dort `width`. Auf dem Handy wirkt sie nie. */
  wideWidth: { type: Number, default: 0 },
  /* Hoeher setzen, wenn ein Dialog ueber einem anderen liegen soll. Die
     Rueckfrage aus ConfirmDialog liegt bei 40/41 und bleibt darueber. */
  z: { type: Number, default: 30 },
});

const emit = defineEmits(['close']);

function onKey(e) {
  if (e.key !== 'Escape') return;
  /* Liegt eine Rueckfrage darueber, gehoert Escape ihr. Sonst wuerde ein
     abgebrochenes Loeschen gleich den ganzen Dialog mit schliessen. */
  if (confirmState.value) return;
  emit('close');
}

onMounted(() => document.addEventListener('keydown', onKey));
onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
</script>

<template>
  <!-- An den Seitenkoerper gehaengt: Wird der Dialog aus einem Bereich heraus
       geoeffnet, der selbst eine Stapelordnung aufmacht — etwa aus der festen
       Kopfleiste —, laege er sonst darin gefangen und verschwaende hinter der
       Spielerakte. -->
  <Teleport to="body">
    <!-- Gemeinsames Wurzelelement nur, damit die Themenklasse (siehe oben)
         einmal gesetzt auf Scrim und Dialogkasten gleichermaßen wirkt — sonst
         ohne eigene Bedeutung, kein position/z-index hier. -->
    <div :class="themeClasses">
      <div class="om-scrim" :style="{ zIndex: z }" @click="emit('close')"></div>

      <div
        class="om-modal"
        :style="{
          zIndex: z + 1,
          '--om-w': `${width}px`,
          '--om-w-wide': `${wideWidth || width}px`,
        }"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-head">
          <h3>{{ title }}</h3>
          <button class="om-close" :title="$t('common.close')" @click="emit('close')">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <slot />
        </div>

        <div v-if="$slots.actions" class="om-actions">
          <slot name="actions" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.om-scrim {
  position: fixed;
  inset: 0;
  background: rgba(4, 6, 9, 0.62);
}
.om-modal {
  width: min(var(--om-w), calc(100vw - 32px));
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 20px 60px var(--shadow-strong);
}
@media (min-width: 1024px) {
  .om-modal {
    width: min(var(--om-w-wide), calc(100vw - 32px));
  }
}
.om-modal .modal-head {
  justify-content: space-between;
  gap: 12px;
  flex: none;
}
.om-modal .modal-body {
  overflow-y: auto;
}
.om-close {
  display: flex;
  padding: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--dim);
  cursor: pointer;
  transition: color 0.14s, background 0.14s;
}
.om-close:hover {
  color: var(--text);
  background: var(--card-hover);
}
.om-actions {
  flex: none;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--border-soft);
}
</style>
